import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { langMessages } from "src/locales/en/lang"

export interface Language {
  languageId: string
  name: string
}

/**
 * Custom hook to get all available languages from the translation file
 * @param options.excludedLanguages - Optional array of language codes to exclude from the list
 * @returns Sorted array of all language codes from lang.ts
 */
export const useFullLanguageList = () => {
  const { t } = useTranslation("lang")

  return useMemo(() => {
    // Get all keys from langMessages except non-language entries
    const excludedCodes = [
      "langSuggestion",
      "auto",
      // Base language codes (use regional variants instead)
      "de",
      "el",
      "en",
      "fr",
      "it",
      "nl",
      "pa",
      "pt",
      "ro",
      "ru",
      "sv",
      // Specific variants not in allowed list
      "kln_ke",
      "ku_arab",
      "tg_cyrl",
      "tlh",
      "zh-cn",
      "zh-tw"
    ]
    const allLanguages = Object.keys(langMessages).filter((key) => !excludedCodes.includes(key))

    return allLanguages
      .map((lang: string) => ({ languageId: lang, name: t(lang) }))
      .sort((a: Language, b: Language) => sortByName(a, b))
  }, [t])
}

export function sortByName(a: Language, b: Language) {
  if (a.name.toLowerCase() < b.name.toLowerCase()) {
    return -1
  }
  if (a.name.toLowerCase() > b.name.toLowerCase()) {
    return 1
  }
  return 0
}
